import React, { useCallback, useEffect, useState } from 'react'
import {
	AutoComplete,
	Button,
	Card,
	Col,
	Input,
	Modal,
	Popconfirm,
	Row,
	Select,
	Space,
	Switch,
	Table,
	Tag,
	Tabs,
	Typography,
	message,
} from 'antd'
import { HistoryOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { useTheme, useThemeColors } from '../../common/ThemeContext'
import { leaveService } from '../../common/service'
import { staffService } from '../../common/service/staff.service'

const { Text } = Typography

const CURRENT_YEAR = new Date().getFullYear()
const YEARS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - 2 + i)

const toList = (payload: any, keys: string[] = []): any[] => {
	if (Array.isArray(payload)) return payload
	for (const k of keys) {
		const v = payload?.[k]
		if (Array.isArray(v)) return v
	}
	if (Array.isArray(payload?.data)) return payload.data
	for (const k of keys) {
		const v = payload?.data?.[k]
		if (Array.isArray(v)) return v
	}
	return []
}

const formatDateTime = (value: any): string => {
	if (!value) return '—'

	if (value instanceof Date) {
		const t = value.getTime()
		return Number.isFinite(t) ? value.toLocaleString() : '—'
	}

	if (typeof value?.toDate === 'function') {
		const dt = value.toDate()
		const t = dt?.getTime?.()
		return Number.isFinite(t) ? dt.toLocaleString() : '—'
	}

	if (typeof value === 'object' && typeof value._seconds === 'number') {
		const ms = value._seconds * 1000 + Math.floor((value._nanoseconds || 0) / 1_000_000)
		const dt = new Date(ms)
		return Number.isFinite(dt.getTime()) ? dt.toLocaleString() : '—'
	}

	const dt = new Date(value)
	return Number.isFinite(dt.getTime()) ? dt.toLocaleString() : '—'
}

interface RunRecord {
	id?: string
	year: number
	runAt: any
	triggeredBy?: string
	dryRun: boolean
	totalProcessed: number
	totalUpdated: number
	totalSkipped?: number
	status?: string
}

interface AuditEntry {
	id?: string
	staffId: string
	staffName?: string
	leaveType: string
	year: number
	previousBalance: number
	carriedAmount: number
	newEntitlement: number
	cap?: number
	runAt: any
}

interface StaffOption {
	value: string
	label: string
	staffId: string
}

const CarryForwardHistory: React.FC = () => {
	const { isDarkMode } = useTheme()
	const themeColors = useThemeColors()
	const { primary, text, textSecondary, bg, border, chartBg } = themeColors
	const navigate = useNavigate()

	// ── Run history ───────────────────────────────────────────────────────
	const [runYear, setRunYear] = useState<number>(CURRENT_YEAR)
	const [runs, setRuns] = useState<RunRecord[]>([])
	const [runsLoading, setRunsLoading] = useState(false)
	const [runPagination, setRunPagination] = useState({
		current: 1,
		pageSize: 20,
		total: 0,
	})

	// ── Audit entries ──────────────────────────────────────────────────────
	const [auditYear, setAuditYear] = useState<number>(CURRENT_YEAR)
	const [auditStaffSearch, setAuditStaffSearch] = useState('')
	const [auditStaffOptions, setAuditStaffOptions] = useState<StaffOption[]>([])
	const [auditStaffId, setAuditStaffId] = useState<string | undefined>(undefined)
	const [auditStaffIdInput, setAuditStaffIdInput] = useState('')
	const [audit, setAudit] = useState<AuditEntry[]>([])
	const [auditLoading, setAuditLoading] = useState(false)
	const [auditPagination, setAuditPagination] = useState({
		current: 1,
		pageSize: 20,
		total: 0,
	})

	// ── Manual trigger ─────────────────────────────────────────────────────
	const [triggerYear, setTriggerYear] = useState<number>(CURRENT_YEAR)
	const [dryRun, setDryRun] = useState(true)
	const [triggerLoading, setTriggerLoading] = useState(false)

	// ── Fetch runs ────────────────────────────────────────────────────────
	const fetchRuns = useCallback(
		async (page?: number, limit?: number) => {
			const current = page ?? runPagination.current
			const pageSize = limit ?? runPagination.pageSize
			setRunsLoading(true)
			try {
				const res = await leaveService.getCarryForwardRuns({
					year: runYear,
					page: current,
					limit: pageSize,
				})
				const rawList: any[] = toList(res, ['runs'])
				const total = Number(res?.total ?? res?.data?.total ?? rawList.length)
				const list: RunRecord[] = rawList.map((r: any) => ({
					id: r.id ?? r.runId,
					year: Number(r.toYear ?? r.year ?? runYear),
					runAt: r.runAt ?? r.startedAt ?? r.completedAt,
					triggeredBy: r.triggeredBy,
					dryRun: Boolean(r.dryRun),
					totalProcessed: Number(r.totalProcessed ?? r.processed ?? 0),
					totalUpdated: Number(r.totalUpdated ?? r.applied ?? 0),
					totalSkipped: Number(
						(r.totalSkipped ?? 0) ||
							Number(r.skippedNoCarry ?? 0) + Number(r.skippedAlreadyProcessed ?? 0),
					),
					status: r.status,
				}))
				setRuns(list)
				setRunPagination({ current, pageSize, total })
			} catch {
				message.error('Failed to load carry-forward run history')
			} finally {
				setRunsLoading(false)
			}
		},
		[runPagination.current, runPagination.pageSize, runYear],
	)

	useEffect(() => {
		fetchRuns(1, runPagination.pageSize)
	}, [runYear])

	// ── Fetch audit ────────────────────────────────────────────────────────
	const fetchAudit = useCallback(
		async (page?: number, limit?: number) => {
			const current = page ?? auditPagination.current
			const pageSize = limit ?? auditPagination.pageSize
			setAuditLoading(true)
			try {
				const res = await leaveService.getCarryForwardAudit({
					year: auditYear,
					staffId: auditStaffId,
					page: current,
					limit: pageSize,
				})
				const rawList: any[] = toList(res, ['entries'])
				const total = Number(res?.total ?? res?.data?.total ?? rawList.length)
				const list: AuditEntry[] = rawList.flatMap((a: any) => {
					const byType = a.carryByType || {}
					const entitlements = a.sourceEntitlements || {}
					const used = a.sourceUsed || {}
					const caps = a.capByType || {}
					return Object.keys(byType).map((leaveType) => {
						const previousBalance = Math.max(
							0,
							Number(entitlements[leaveType] || 0) - Number(used[leaveType] || 0),
						)
						const carriedAmount = Number(byType[leaveType] || 0)
						return {
							id: `${a.id || a.runId || 'audit'}_${a.staffId}_${leaveType}`,
							staffId: a.staffId,
							staffName: a.staffName,
							leaveType,
							year: Number(a.toYear ?? a.year ?? auditYear),
							previousBalance,
							carriedAmount,
							newEntitlement: Number(entitlements[leaveType] || 0) + carriedAmount,
							cap: caps[leaveType] != null ? Number(caps[leaveType]) : undefined,
							runAt: a.runAt ?? a.createdAt,
						}
					})
				})
				setAudit(list)
				setAuditPagination({ current, pageSize, total })
			} catch {
				message.error('Failed to load carry-forward audit entries')
			} finally {
				setAuditLoading(false)
			}
		},
		[auditPagination.current, auditPagination.pageSize, auditStaffId, auditYear],
	)

	useEffect(() => {
		fetchAudit(1, auditPagination.pageSize)
	}, [auditStaffId, auditYear])

	// ── Staff autocomplete for audit filter ────────────────────────────────
	const handleAuditStaffSearch = async (val: string) => {
		setAuditStaffSearch(val)
		if (!val.trim()) {
			setAuditStaffOptions([])
			return
		}
		try {
			const res = await staffService.fetchListing({ name: val, limit: 10 })
			const items: any[] = res?.data?.staff ?? res?.data?.data ?? res?.data ?? []
			setAuditStaffOptions(
				items.map((s: any) => ({
					value: s.name ?? s.fullName ?? s.staffId ?? s._id,
					label: `${s.name ?? s.fullName ?? s.staffId} (${s.staffId ?? s._id})`,
					staffId: s.staffId ?? s._id,
				})),
			)
		} catch {
			// silently ignore autocomplete errors
		}
	}

	// ── Manual trigger ─────────────────────────────────────────────────────
	const handleTrigger = async () => {
		setTriggerLoading(true)
		try {
			const res = await leaveService.triggerCarryForward({ year: triggerYear, dryRun })
			const result = res?.data ?? res
			const msg = dryRun
				? `Dry-run complete: would process ${result?.totalProcessed ?? '?'} staff, update ${result?.totalUpdated ?? '?'} records.`
				: `Carry-forward executed: ${result?.totalUpdated ?? '?'} records updated for ${triggerYear}.`
			Modal.success({
				title: dryRun ? 'Dry-Run Result' : 'Carry-Forward Complete',
				content: msg,
			})
			if (!dryRun) fetchRuns(1, runPagination.pageSize)
		} catch {
			message.error('Failed to trigger carry-forward job')
		} finally {
			setTriggerLoading(false)
		}
	}

	// ── Run history columns ────────────────────────────────────────────────
	const runColumns: ColumnsType<RunRecord> = [
		{
			title: 'Run At',
			dataIndex: 'runAt',
			key: 'runAt',
			render: (v: any) => formatDateTime(v),
		},
		{
			title: 'Year',
			dataIndex: 'year',
			key: 'year',
			width: 80,
			align: 'center',
		},
		{
			title: 'Dry Run',
			dataIndex: 'dryRun',
			key: 'dryRun',
			width: 90,
			align: 'center',
			render: (v: boolean) =>
				v ? <Tag color='orange'>Dry Run</Tag> : <Tag color='green'>Live</Tag>,
		},
		{
			title: 'Processed',
			dataIndex: 'totalProcessed',
			key: 'totalProcessed',
			width: 110,
			align: 'center',
		},
		{
			title: 'Updated',
			dataIndex: 'totalUpdated',
			key: 'totalUpdated',
			width: 100,
			align: 'center',
			render: (v: number) => <Tag color={v > 0 ? 'blue' : 'default'}>{v}</Tag>,
		},
		{
			title: 'Skipped',
			dataIndex: 'totalSkipped',
			key: 'totalSkipped',
			width: 100,
			align: 'center',
			render: (v: number | undefined) => (v != null ? v : '—'),
		},
		{
			title: 'Triggered By',
			dataIndex: 'triggeredBy',
			key: 'triggeredBy',
			render: (v?: string) => v ?? <Text type='secondary'>Scheduler</Text>,
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 100,
			render: (v?: string) => {
				if (!v) return null
				const color = v === 'completed' ? 'green' : v === 'failed' ? 'red' : 'default'
				return <Tag color={color}>{v}</Tag>
			},
		},
	]

	// ── Audit columns ──────────────────────────────────────────────────────
	const auditColumns: ColumnsType<AuditEntry> = [
		{
			title: 'Run At',
			dataIndex: 'runAt',
			key: 'runAt',
			render: (v: any) => formatDateTime(v),
		},
		{
			title: 'Staff ID',
			dataIndex: 'staffId',
			key: 'staffId',
			width: 120,
		},
		{
			title: 'Name',
			dataIndex: 'staffName',
			key: 'staffName',
			render: (v?: string) => v ?? '—',
		},
		{
			title: 'Leave Type',
			dataIndex: 'leaveType',
			key: 'leaveType',
			render: (v: string) => <Text strong>{v}</Text>,
		},
		{
			title: 'Year',
			dataIndex: 'year',
			key: 'year',
			width: 80,
			align: 'center',
		},
		{
			title: 'Prev Balance',
			dataIndex: 'previousBalance',
			key: 'previousBalance',
			width: 120,
			align: 'center',
		},
		{
			title: 'Carried Over',
			dataIndex: 'carriedAmount',
			key: 'carriedAmount',
			width: 120,
			align: 'center',
			render: (v: number) => <Tag color={v > 0 ? 'blue' : 'default'}>{v}</Tag>,
		},
		{
			title: 'Cap',
			dataIndex: 'cap',
			key: 'cap',
			width: 80,
			align: 'center',
			render: (v?: number) => (v != null ? v : '—'),
		},
		{
			title: 'New Entitlement',
			dataIndex: 'newEntitlement',
			key: 'newEntitlement',
			width: 140,
			align: 'center',
			render: (v: number) => <Tag color='green'>{v}</Tag>,
		},
	]

	return (
		<div style={{ minHeight: '100vh', background: chartBg }}>
			<GlobalHeader />

			{/* ── Page Header ── */}
			<div
				style={{
					background: bg,
					borderBottom: `1px solid ${border}`,
					padding: '14px 24px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: 10,
							background: isDarkMode ? `${primary}15` : primary,
							color: isDarkMode ? primary : '#fff',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
						}}
					>
						<HistoryOutlined style={{ fontSize: 24 }} />
					</div>
					<div>
						<div
							style={{
								fontSize: 20,
								fontWeight: 700,
								color: text,
								lineHeight: '1.2',
							}}
						>
							Carry Forward History
						</div>
						<div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
							View year-end carry-forward run logs and per-staff audit entries
						</div>
					</div>
				</div>
				<Button onClick={() => navigate('/leaves/balance')}>← Back to Balance</Button>
			</div>

			<div style={{ padding: '20px 24px' }}>
				<Tabs
					defaultActiveKey='runs'
					items={[
						{
							key: 'runs',
							label: 'Run History',
							children: (
								<Card
									style={{
										borderRadius: 8,
										boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
									}}
									styles={{ body: { padding: '16px 20px' } }}
								>
									<Row
										gutter={[16, 12]}
										align='middle'
										style={{ marginBottom: 16 }}
									>
										<Col xs={24} sm={6}>
											<Text
												type='secondary'
												style={{
													fontSize: 12,
													display: 'block',
													marginBottom: 6,
												}}
											>
												FILTER BY YEAR
											</Text>
											<Select
												value={runYear}
												onChange={setRunYear}
												options={YEARS.map((y) => ({
													value: y,
													label: String(y),
												}))}
												style={{ width: '100%' }}
											/>
										</Col>
										<Col
											xs={24}
											sm={18}
											style={{ display: 'flex', alignItems: 'flex-end' }}
										>
											<Button
												icon={<ReloadOutlined />}
												onClick={() =>
													fetchRuns(
														runPagination.current,
														runPagination.pageSize,
													)
												}
												loading={runsLoading}
											>
												Refresh
											</Button>
										</Col>
									</Row>
									<Table<RunRecord>
										dataSource={runs}
										columns={runColumns}
										rowKey={(r, i) => r.id ?? String(i)}
										loading={runsLoading}
										pagination={{
											current: runPagination.current,
											pageSize: runPagination.pageSize,
											total: runPagination.total,
											showSizeChanger: true,
											pageSizeOptions: ['10', '20', '50'],
											onChange: (page, pageSize) => fetchRuns(page, pageSize),
										}}
										size='middle'
										locale={{
											emptyText: 'No carry-forward runs found for this year.',
										}}
										scroll={{ x: 900 }}
									/>
								</Card>
							),
						},
						{
							key: 'audit',
							label: 'Per-Staff Audit Log',
							children: (
								<Card
									style={{
										borderRadius: 8,
										boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
									}}
									styles={{ body: { padding: '16px 20px' } }}
								>
									<Row
										gutter={[16, 12]}
										align='bottom'
										style={{ marginBottom: 16 }}
									>
										<Col xs={24} sm={5}>
											<Text
												type='secondary'
												style={{
													fontSize: 12,
													display: 'block',
													marginBottom: 6,
												}}
											>
												YEAR
											</Text>
											<Select
												value={auditYear}
												onChange={(v) => {
													setAuditYear(v)
												}}
												options={YEARS.map((y) => ({
													value: y,
													label: String(y),
												}))}
												style={{ width: '100%' }}
											/>
										</Col>
										<Col xs={24} sm={7}>
											<Text
												type='secondary'
												style={{
													fontSize: 12,
													display: 'block',
													marginBottom: 6,
												}}
											>
												FILTER BY STAFF NAME
											</Text>
											<AutoComplete
												options={auditStaffOptions}
												value={auditStaffSearch}
												onSearch={handleAuditStaffSearch}
												onSelect={(_value, option: any) => {
													setAuditStaffSearch(option.value)
													setAuditStaffId(option.staffId)
													setAuditStaffIdInput('')
												}}
												onClear={() => {
													setAuditStaffSearch('')
													setAuditStaffId(undefined)
													setAuditStaffOptions([])
												}}
												allowClear
												placeholder='Search by name...'
												style={{ width: '100%' }}
											/>
										</Col>
										<Col xs={24} sm={7}>
											<Text
												type='secondary'
												style={{
													fontSize: 12,
													display: 'block',
													marginBottom: 6,
												}}
											>
												OR BY STAFF ID
											</Text>
											<Input.Search
												placeholder='Enter Staff ID...'
												value={auditStaffIdInput}
												allowClear
												enterButton='Filter'
												onChange={(e) => {
													const val = e.target.value
													setAuditStaffIdInput(val)
													if (!val.trim()) setAuditStaffId(undefined)
												}}
												onSearch={(val) => {
													const id = val.trim()
													setAuditStaffSearch('')
													setAuditStaffOptions([])
													setAuditStaffId(id || undefined)
												}}
												style={{ width: '100%' }}
											/>
										</Col>
										<Col xs={24} sm={5}>
											<Button
												icon={<ReloadOutlined />}
												onClick={() =>
													fetchAudit(
														auditPagination.current,
														auditPagination.pageSize,
													)
												}
												loading={auditLoading}
											>
												Refresh
											</Button>
										</Col>
									</Row>
									<Table<AuditEntry>
										dataSource={audit}
										columns={auditColumns}
										rowKey={(r, i) => r.id ?? String(i)}
										loading={auditLoading}
										pagination={{
											current: auditPagination.current,
											pageSize: auditPagination.pageSize,
											total: auditPagination.total,
											showSizeChanger: true,
											pageSizeOptions: ['10', '20', '50'],
											onChange: (page, pageSize) =>
												fetchAudit(page, pageSize),
										}}
										size='middle'
										locale={{ emptyText: 'No audit entries found.' }}
										scroll={{ x: 1100 }}
									/>
								</Card>
							),
						},
						{
							key: 'trigger',
							label: 'Manual Trigger',
							children: (
								<Card
									style={{
										borderRadius: 8,
										boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
										maxWidth: 540,
									}}
									styles={{ body: { padding: '24px 28px' } }}
								>
									<Text strong style={{ fontSize: 15 }}>
										Manually Run Carry-Forward Job
									</Text>
									<div
										style={{
											marginTop: 4,
											marginBottom: 20,
											color: '#64748b',
											fontSize: 13,
										}}
									>
										Use this to trigger the year-end carry-forward outside of
										the automatic scheduler. Always test with a Dry Run first.
									</div>

									<Row gutter={[16, 16]}>
										<Col xs={24} sm={12}>
											<Text
												type='secondary'
												style={{
													fontSize: 12,
													display: 'block',
													marginBottom: 6,
												}}
											>
												TARGET YEAR
											</Text>
											<Select
												value={triggerYear}
												onChange={setTriggerYear}
												options={YEARS.map((y) => ({
													value: y,
													label: String(y),
												}))}
												style={{ width: '100%' }}
												size='large'
											/>
										</Col>
										<Col xs={24} sm={12}>
											<Text
												type='secondary'
												style={{
													fontSize: 12,
													display: 'block',
													marginBottom: 6,
												}}
											>
												MODE
											</Text>
											<Space align='center' style={{ marginTop: 6 }}>
												<Switch checked={dryRun} onChange={setDryRun} />
												<Text>
													{dryRun
														? 'Dry Run (simulation only)'
														: 'Live Run (writes to DB)'}
												</Text>
											</Space>
										</Col>
									</Row>

									<div style={{ marginTop: 24 }}>
										<Popconfirm
											title={
												dryRun
													? 'Run carry-forward simulation?'
													: 'Run carry-forward LIVE?'
											}
											description={
												dryRun
													? `This is a simulation for ${triggerYear}. No data will be changed.`
													: `This will write carry-forward balances to the database for ${triggerYear}. Continue?`
											}
											onConfirm={handleTrigger}
											okText={dryRun ? 'Run Simulation' : 'Run Live'}
											okButtonProps={{ danger: !dryRun }}
											cancelText='Cancel'
										>
											<Button
												type='primary'
												danger={!dryRun}
												icon={<PlayCircleOutlined />}
												loading={triggerLoading}
												size='large'
											>
												{dryRun
													? 'Run Dry-Run Simulation'
													: 'Run Live Carry-Forward'}
											</Button>
										</Popconfirm>
									</div>
								</Card>
							),
						},
					]}
				/>
			</div>
		</div>
	)
}

export default CarryForwardHistory
